import React, { useMemo, useState } from 'react';
import { Inbox, Clock, Eye, CheckCircle2, XCircle, AlertCircle, Sparkles } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { FilterPill } from '@/components/ui/FilterPill';
import { InstructorReviewCard } from '../components/InstructorReviewCard';
import {
  useInstructorReviews,
  getInstructorStatus,
  InstructorReviewStatus,
} from '../hooks/useInstructorReviews';
import { TrainingVideo } from '@/types/videoAnnotations';
import { useI18n } from '@/contexts/I18nContext';
import { RollReviewToolsGate } from '@/features/subscription/components/RollReviewToolsGate';

// ── Empty state per tab ───────────────────────────────────────────────────

type TabKey = 'all' | InstructorReviewStatus;

const EMPTY_CONFIG: Record<
  TabKey,
  { Icon: React.FC<{ size?: number; className?: string }>; color: string; titleKey: string; descKey: string }
> = {
  all:       { Icon: Inbox,        color: 'bg-white/5',       titleKey: 'manage_reviews.empty_all_title',       descKey: 'manage_reviews.empty_all_desc' },
  pending:   { Icon: Clock,        color: 'bg-amber-500/10',  titleKey: 'manage_reviews.empty_pending_title',   descKey: 'manage_reviews.empty_pending_desc' },
  in_review: { Icon: Eye,          color: 'bg-blue-500/10',   titleKey: 'manage_reviews.empty_in_review_title', descKey: 'manage_reviews.empty_in_review_desc' },
  delivered: { Icon: CheckCircle2, color: 'bg-green-500/10',  titleKey: 'manage_reviews.empty_delivered_title', descKey: 'manage_reviews.empty_delivered_desc' },
  declined:  { Icon: XCircle,      color: 'bg-red-500/10',    titleKey: 'manage_reviews.empty_declined_title',  descKey: 'manage_reviews.empty_declined_desc' },
  cancelled: { Icon: XCircle,      color: 'bg-white/5',       titleKey: 'manage_reviews.empty_declined_title',  descKey: 'manage_reviews.empty_declined_desc' },
  expired:   { Icon: AlertCircle,  color: 'bg-white/5',       titleKey: 'manage_reviews.empty_expired_title',   descKey: 'manage_reviews.empty_expired_desc' },
};

const EmptyState: React.FC<{ tab: TabKey; t: ReturnType<typeof useI18n>['t'] }> = ({ tab, t }) => {
  const cfg = EMPTY_CONFIG[tab] ?? EMPTY_CONFIG.all;
  const { Icon, color } = cfg;
  return (
    <div className="flex flex-col items-center justify-center min-h-[52vh] text-center gap-5 px-8">
      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${color}`}>
        <Icon size={32} className="text-white/30" />
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-white/60 text-sm font-medium">{t(cfg.titleKey)}</p>
        <p className="text-white/30 text-xs leading-relaxed">{t(cfg.descKey)}</p>
      </div>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────

const ManageReviews: React.FC = () => {
  const { t } = useI18n();
  const {
    reviews,
    isLoading,
    isAtLimit,
    acceptReview,
    declineReview,
    isAccepting,
    isDeclining,
    acceptingId,
    decliningId,
  } = useInstructorReviews();

  const [activeTab, setActiveTab] = useState<TabKey>('all');

  // ── Count per status ────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const map: Partial<Record<TabKey, number>> = { all: reviews.length };
    for (const v of reviews) {
      const s = getInstructorStatus(v);
      map[s] = (map[s] ?? 0) + 1;
    }
    return map;
  }, [reviews]);

  // ── Filtered list ───────────────────────────────────────────────────────
  const visible: TrainingVideo[] = useMemo(() => {
    if (activeTab === 'all') return reviews;
    return reviews.filter((v) => getInstructorStatus(v) === activeTab);
  }, [reviews, activeTab]);

  // ── Tab definitions ─────────────────────────────────────────────────────
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all',       label: t('manage_reviews.filter_all') },
    { key: 'pending',   label: t('manage_reviews.filter_pending') },
    { key: 'in_review', label: t('manage_reviews.filter_in_review') },
    { key: 'delivered', label: t('manage_reviews.filter_delivered') },
    { key: 'declined',  label: t('manage_reviews.filter_declined') },
    { key: 'expired',   label: t('manage_reviews.filter_expired') },
  ];

  // ── Render ───────────────────────────────────────────────────────────────
  // Gated by RollReviewToolsGate (Phase 7 / SOS-332): Free instructors see the
  // premium trigger sheet auto-open over a locked placeholder; Pro instructors
  // see the real inbox below.
  return (
    <SubPageLayout title={t('manage_reviews.title')} hideBottomNav={false}>
      <RollReviewToolsGate
        origin="roll_review_block"
        fallback={
          <div className="flex flex-col items-center justify-center min-h-[52vh] text-center gap-5 px-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-[#D72E16]/15">
              <Sparkles size={32} className="text-[#D72E16]" />
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-white/80 text-sm font-semibold">{t('premium.trigger.locked_title')}</p>
              <p className="text-white/40 text-xs leading-relaxed">{t('premium.trigger.locked_body')}</p>
            </div>
          </div>
        }
      >

      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {tabs.map(({ key, label }) => {
          const count = counts[key] ?? 0;
          if (count === 0 && key !== 'all' && key !== 'pending') return null;
          return (
            <FilterPill
              key={key}
              label={label}
              count={key !== 'all' ? count : undefined}
              active={activeTab === key}
              onClick={() => setActiveTab(key)}
              testid="filter-pill"
            />
          );
        })}
      </div>

      {/* Content */}
      <div className="mt-4">
        {isLoading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-sossiety-surface rounded-2xl p-4 h-32 animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState tab={activeTab} t={t} />
        ) : (
          <div className="flex flex-col gap-3">
            {visible.map((video) => (
              <InstructorReviewCard
                key={video.id}
                video={video}
                onAccept={acceptReview}
                onDecline={declineReview}
                isAccepting={isAccepting && acceptingId === video.id}
                isDeclining={isDeclining && decliningId === video.id}
                isAtLimit={isAtLimit && getInstructorStatus(video) === 'pending'}
              />
            ))}
          </div>
        )}
      </div>
      </RollReviewToolsGate>
    </SubPageLayout>
  );
};

export default ManageReviews;
